import React from 'react';

import { Box } from '@components/Restyle';
import { Skeleton } from '@components/shared/Skeleton';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';

/** Tasks / Checklist / Files & Media / Activity Log / Members — mirrors the
 * label set `CustomTabBarProject.getIcon` switches on, in tab order. Widths
 * are eyeballed against each label's rendered length so the skeleton reads
 * as text-shaped rather than uniform bars. */
const TAB_LABELS = [
  { label: 'Tasks', width: 34 },
  { label: 'Checklist', width: 56 },
  { label: 'Files & Media', width: 76 },
  { label: 'Activity Log', width: 68 },
  { label: 'Members', width: 52 },
];

const INVITE_BUTTON_HEIGHT = getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT);

/**
 * Per-region placeholder for the web Project Detail tab shell (TA-10616),
 * mirroring `project-detail-tabs.web.tsx` region for region: the description
 * card, the tab bar (all five tab items plus the trailing Members count
 * badge), and the header's link/invite controls.
 *
 * `ProjectDetailTabs` used to gate this whole region — including the tab bar
 * itself — behind a full-screen `ActivityIndicatorLoading` while `getProject`
 * was in flight, on every mount, even with a warm cache, because it reads the
 * query `cache-and-network`. This renders in place of that region only, and
 * is built entirely from non-interactive `Box`/`Skeleton` primitives (no
 * `TouchableOpacity`/`onPress`) so the tabs cannot be tapped before the real
 * data has loaded.
 *
 * The active tab's own body content (Activity Log rows, task rows, etc.) is
 * intentionally left out — the shell doesn't know which tab is active before
 * `Tabs.Navigator` mounts, and each tab screen already owns its own body
 * skeleton.
 */
export const ProjectDetailTabsSkeleton: React.FC<{ testID?: string }> = ({
  testID = 'project-detail-tabs-skeleton',
}) => {
  return (
    <Box testID={testID} flex={1}>
      {/* Description card: section label then two body lines. */}
      <Box
        backgroundColor='grey01'
        borderRadius='s'
        padding='m'
        mx='s'
        marginBottom='xs'>
        <Skeleton variant='text' width={90} />
        <Box marginTop='m'>
          <Skeleton variant='text' width='100%' />
        </Box>
        <Box marginTop='xxs'>
          <Skeleton variant='text' width='70%' />
        </Box>
      </Box>

      {/* Tab bar: five tab items on the left, link icon + Invite pill on the right.
          height matches CustomTabBarProject's live 56px row (TA-10401) so
          swapping the skeleton for the real tab bar doesn't shift the page. */}
      <Box marginVertical='s'>
        <Box
          height={getScaledSize(56)}
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          borderBottomWidth={1}
          borderBottomColor='grey02'>
          <Box
            flexDirection='row'
            alignItems='center'
            marginHorizontal='s'
            style={{ gap: theme.spacing.m }}>
            {TAB_LABELS.map(({ label, width }) => (
              <Box
                key={label}
                flexDirection='row'
                alignItems='center'
                paddingVertical='xs'>
                <Skeleton
                  variant='rect'
                  width={16}
                  height={16}
                  borderRadius='xxs'
                />
                <Box marginLeft='xxs'>
                  <Skeleton variant='text' width={width} />
                </Box>
                {label === 'Members' && (
                  <Box marginLeft='xxs'>
                    <Skeleton
                      variant='circle'
                      width={18}
                      height={18}
                      borderRadius='xxl'
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
          <Box flexDirection='row' alignItems='center' marginRight='m'>
            <Box padding='xxs' borderRadius='xs' marginRight='xs'>
              <Skeleton
                variant='rect'
                width={24}
                height={24}
                borderRadius='xs'
              />
            </Box>
            <Skeleton
              variant='rect'
              width={110}
              height={INVITE_BUTTON_HEIGHT}
              borderRadius='l'
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
